import isNil from 'lodash.isnil';
import request from '../../request';
import { constructUrlObject } from '../../../utils/shared/urlUtils';
import WEB_ENTRY from '../../../constants/shared/WebEntryConstants';
import { PARAM } from '../../../constants/shared/ApiConstants';

const {
    PERIOD,
    GRADE,
    FIELDS,
    FORM_ID,
    TOTAL_ITEMS,
    SEQUENCE,
    ASSESSMENT,
    CREATE_DATE,
    DATA_SOURCE,
    INST_UID,
    STAFF_UID,
    STUDENT_UID,
    STUDENT_TEMPORAL_ID,
    SCHOOL_YEAR,
    GRADE_LEVEL,
    MEASURE,
    CORRECT_RESPONSES,
    INCORRECT_RESPONSES,
    FORM_NUMBER,
} = PARAM;

/**
 * @typedef {Object} BasicComprehensionURLs
 * @property {string} forms
 * @property {string} interps
 * @property {string} createResult
 * @property {string} deleteResult
 */

/** Class representing a shared Basic Comprehension client to be extended by Reading MAZE and Espanol CLPL clients.
 * This class can't be instantiated.
 */
class BasicComprehensionClient {
    /**
     * @param {string} assessment
     * @param {string} measure
     * @param {string} stringZero
     * @param {BasicComprehensionURLs} urls
     */
    constructor(assessment, measure, stringZero, urls) {
        this.assessment = assessment;
        this.measure = measure;
        this.stringZero = stringZero;
        this.urls = urls;

        if (this.constructor.name === 'BasicComprehensionClient') {
            throw new Error('BasicComprehensionClient class can only be extended');
        }
    }

    getForm = formId => {
        return request.get(`${this.urls.forms}/${formId}`);
    };

    getFormIdsByGradesPeriod = (grades, period) => {
        const urlWithQueryParams = constructUrlObject(this.urls.forms, {
            [PERIOD]: period,
            [GRADE]: grades.join(','),
            [FIELDS]: [GRADE, FORM_ID, TOTAL_ITEMS, SEQUENCE].join(','),
        });
        return request.get(urlWithQueryParams);
    };

    getAllForms = () => {
        return request.get(this.urls.forms);
    };

    getInterps = () => {
        return request.get(this.urls.interps);
    };

    deleteResult = resultId => {
        return request.delete(`${this.urls.deleteResult}/${resultId}`);
    };

    getBasicComprehensionResult({
        date,
        schoolId,
        staffId,
        studentId,
        year,
        grade,
        period,
        formId,
        correctResponses,
        incorrectResponses,
        formNumber,
        temporalStudentId,
    }) {
        const result = {
            [ASSESSMENT]: this.assessment,
            [CREATE_DATE]: date,
            [DATA_SOURCE]: WEB_ENTRY,
            [INST_UID]: schoolId,
            [STAFF_UID]: staffId,
            [STUDENT_UID]: studentId,
            [STUDENT_TEMPORAL_ID]: temporalStudentId || this.stringZero,
            [SCHOOL_YEAR]: year,
            [GRADE_LEVEL]: grade,
            [PERIOD]: period,
            [MEASURE]: this.measure,
            [FORM_ID]: formId,
            [CORRECT_RESPONSES]: correctResponses,
            [INCORRECT_RESPONSES]: incorrectResponses,
        };
        if (!isNil(formNumber)) {
            result[FORM_NUMBER] = formNumber;
        }
        return result;
    }
}

export default BasicComprehensionClient;
